import { useFormSteps } from '@features/onboarding/hooks/useFormSteps';
import { OnboardingSchema } from '@features/onboarding/schema/onboarding';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useFormNavigation } from '@features/onboarding/components/shared/lib/contexts/FormNavigationContext';
import { ONBOARDING_STEP_KEY } from '@constants/index';
import { useApplicationStore } from 'src/store/application';
import {
  FormStepSections,
  FormStepID,
} from '@features/onboarding/enums/formSections.enum';

export const useFormControls = () => {
  const {
    currentStep,
    totalSteps,
    isSliderStep,
    activeSliderTab,
    totalSliderTabs,
    setCurrentStep,
    setTotalSteps,
    setSliderStep,
    setActiveSliderTab,
    setTotalSliderTabs,
    data: formData,
    setData,
    resetData,
    availableSteps,
    setAvailableSteps,
    setCurrentStepId,
    setActiveDentalVisionCategory,
    setLastSelectedExtraPlan,
  } = useOnboardingStore();

  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const formSteps = useFormSteps();
  const currentFormStep = formSteps.at(currentStep);
  const { isMainPlanRejected } = useApplicationStore();
  const prevStepsLengthRef = useRef(formSteps.length);
  const currentStepIdRef = useRef(currentFormStep?.id);

  // Update current step ID ref whenever current step changes
  useLayoutEffect(() => {
    if (currentFormStep?.id) {
      currentStepIdRef.current = currentFormStep.id;
      console.log('currentFormStep.id', currentFormStep.id);
      setCurrentStepId(currentFormStep.id);
    } else {
      setCurrentStepId(null);
    }
  }, [currentFormStep?.id, setCurrentStepId]);

  useEffect(() => {
    return () => {
      setCurrentStepId(null);
    };
  }, [setCurrentStepId]);

  // Adjust currentStep when formSteps array changes (e.g., when isMainPlanRejected changes)
  useEffect(() => {
    const prevStepsLength = prevStepsLengthRef.current;
    const currentStepsLength = formSteps.length;

    // If steps array length changed, we need to re-find current step by ID
    if (prevStepsLength !== currentStepsLength && currentStepIdRef.current) {

      const newIndex = formSteps.findIndex(
        (step) => step.id === currentStepIdRef.current
      );

      if (newIndex !== -1 && newIndex !== currentStep) {
        setCurrentStep(newIndex);
        localStorage.setItem(ONBOARDING_STEP_KEY, newIndex.toString());
      } else if (newIndex === -1) {
        // Current step ID not found (e.g., user was on HealthPlans which is now removed)
        // But make sure we're within bounds
        if (currentStep >= formSteps.length) {
          const safeIndex = Math.max(formSteps.length - 1, 0);
          setCurrentStep(safeIndex);
          localStorage.setItem(ONBOARDING_STEP_KEY, safeIndex.toString());
        }
      }
    }

    prevStepsLengthRef.current = currentStepsLength;
  }, [formSteps.length, formSteps, currentStep]);

  const setSlide = (newDirection: 1 | -1) => {
    console.log('[useFormControls] setSlide called:', {
      newDirection,
      currentStep,
      currentStepId: formSteps[currentStep]?.id,
      isSliderStep,
      activeSliderTab,
      totalSliderTabs,
    });

    if (isSliderStep) {
      const newTab = activeSliderTab + newDirection;
      if (newTab >= 0 && newTab < totalSliderTabs) {
        setActiveSliderTab(newTab);
        return;
      }
      if (newTab < 0) {
        setSliderStep(false);
        setActiveSliderTab(0);
        setTotalSliderTabs(0);
        const newStep = currentStep - 1;
        setCurrentStep(newStep);
        localStorage.setItem(ONBOARDING_STEP_KEY, newStep.toString());
        return;
      }
      if (newTab >= totalSliderTabs) {
        setSliderStep(false);
        setActiveSliderTab(0);
        setTotalSliderTabs(0);
      }
    }

    let newStep = currentStep + newDirection;
    if (newStep < 0 || newStep >= formSteps.length) {
      console.log('[useFormControls] New step out of bounds, aborting');
      return;
    }

    // Skip EstimateResult (Approved) step if already seen
    const { estimateResultSeen } = useOnboardingStore.getState();
    if (
      estimateResultSeen &&
      formSteps[newStep]?.id === FormStepID.Approved
    ) {
      newStep += newDirection;
      if (newStep < 0 || newStep >= formSteps.length) {
        return;
      }
    }

    // Reset dental/vision category when navigating between steps
    // Forward: first tab. Backward: last tab (use -1 as sentinel).
    if (newDirection === 1) {
      setActiveDentalVisionCategory(0);
      setLastSelectedExtraPlan(null);
    } else {
      setActiveDentalVisionCategory(-1);
      setLastSelectedExtraPlan(null);
    }
    setCurrentStep(newStep);

    localStorage.setItem(ONBOARDING_STEP_KEY, newStep.toString());
  };

  // Default handlers
  const defaultHandleNextStep = (data: Partial<OnboardingSchema>) => {
    console.trace('[useFormControls] Stack trace for handleNextStep');
    setData(data);
    setSlide(1);
  };

  const defaultHandlePreviousStep = () => {
    setSlide(-1);
  };

  const defaultHandleSliderTabChange = (tab: number) => {
    if (isSliderStep) {
      setActiveSliderTab(tab);
    }
  };

  const handleJumpTo = (value: string) => {
    console.trace('[useFormControls] Stack trace for handleJumpTo');
    // Проверяем, является ли это переключением между категориями планов в Dental and Vision форме
    const dentalVisionCategories = [
      'Vision Plans',
      'Accident Plans',
      'Critical Illness Plans',
      'Hospital Indemnity Plans',
    ];

    if (
      dentalVisionCategories.includes(value) ||
      value === FormStepSections.Dental
    ) {
      const dentalFormIndex = formSteps.findIndex(
        (item) => item.sectionName === FormStepSections.Dental
      );
      if (dentalFormIndex === -1) {
        console.warn('[useFormControls] Dental form not found');
        return;
      }

      setCurrentStep(dentalFormIndex);
      setSliderStep(false);
      setActiveSliderTab(0);
      setTotalSliderTabs(0);

      // Сохраняем состояние шага в localStorage
      localStorage.setItem(ONBOARDING_STEP_KEY, dentalFormIndex.toString());

      return;
    }

    // Обычная логика для других секций
    const index = formSteps.findIndex((item) => item.sectionName === value);
    if (index === -1) {
      console.log('[useFormControls] Section not found, aborting jump');
      return;
    }

    // Check if we're already on the target step
    if (index === currentStep) {
      return;
    }

    setCurrentStep(index);
    setSliderStep(false);
    setActiveSliderTab(0);
    setTotalSliderTabs(0);
    // Сохраняем состояние шага в localStorage
    localStorage.setItem(ONBOARDING_STEP_KEY, index.toString());
  };

  // Initialize navigation properties with defaults
  let handleNextStep = defaultHandleNextStep;
  let handlePreviousStep = defaultHandlePreviousStep;
  let handleSliderTabChange = defaultHandleSliderTabChange;

  // Try to get navigation handlers from context
  try {
    const navigationProps = useFormNavigation();
    handleNextStep = (data: Partial<OnboardingSchema>) => {
      setData(data);
      navigationProps.handleNext();
    };
    handlePreviousStep = navigationProps.handlePrevious;
    handleSliderTabChange = navigationProps.handleSliderTabChange;
  } catch {
    // If context is not provided, use default handlers
  }

  return {
    selectedStep: currentStep,
    formData,
    currentFormStep,
    formSteps,
    isSliderStep,
    activeSliderTab,
    totalSliderTabs,
    availableSteps,
    isLocalLoading,
    setIsLocalLoading,
    setData,
    resetData,
    setTotalSteps,
    setCurrentStep,
    defaultHandlePreviousStep,
    setSlide,
    handlePreviousStep,
    handleNextStep,
    handleJumpTo,
    handleSliderTabChange,
    setAvailableSteps,
    setSliderStep,
    setActiveSliderTab,
    setTotalSliderTabs,
  };
};
